import { type FC, useEffect, useRef, useState } from "react";

import { listIssueIntegrationProvidersAsUser } from "../../dashboard-client";
import { useJiraSetupView } from "../../hooks/useJiraSetupView";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import {
  ConnectUrlBlock,
  ForgeInstallCommandBlock,
  InstalledView,
  SetupSteps,
} from "../common/JiraSetupView";
import { LoadingSpinner } from "../common/LoadingState";
import { JiraLinkCodeBlock } from "../jira/JiraLinkCodeBlock";

type SetupSubStep = "install" | "link-code";

const POLL_INTERVAL_MS = 5000;

export const IssueIntegrationsPanel: FC = () => {
  const { view, retry } = useJiraSetupView();
  const [subStep, setSubStep] = useState<SetupSubStep>("install");
  const [hasCode, setHasCode] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryRef = useRef(retry);
  retryRef.current = retry;

  // Poll for a linked Jira provider once a code is generated
  useEffect(() => {
    if (!hasCode) {
      return;
    }

    const poll = async () => {
      try {
        const { data } = await listIssueIntegrationProvidersAsUser();
        if (data.providers.some((p) => p.type === "jira")) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          setHasCode(false);
          retryRef.current();
        }
      } catch {
        /* Ignore poll errors */
      }
    };

    void poll();
    pollRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasCode]);

  if (view.status === "loading") {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (view.status === "error") {
    return (
      <div className="space-y-4">
        <Alert variant="warning">{view.message}</Alert>
        <Button type="button" variant="secondary" onClick={retry}>
          Retry
        </Button>
      </div>
    );
  }

  if (view.status === "not-installed") {
    return (
      <div className="space-y-4">
        <EmptyState
          title="Jira not connected"
          description="Connect Jira to let Arti receive and work on issues. Install the Arti app in your Jira instance to get started."
          className="py-8"
        />

        <SetupSteps currentStep={subStep === "install" ? 0 : 1} />

        {subStep === "install" && (
          <>
            {view.connectUrl ? (
              <ConnectUrlBlock connectUrl={view.connectUrl} />
            ) : (
              <ForgeInstallCommandBlock />
            )}
            <Button onClick={() => setSubStep("link-code")} fullWidth>
              I&apos;ve installed the app
            </Button>
          </>
        )}

        {subStep === "link-code" && (
          <>
            <JiraLinkCodeBlock
              autoGenerate
              onCodeGenerated={() => setHasCode(true)}
            />

            {hasCode && (
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-white/40">
                <LoadingSpinner size="sm" />
                Waiting for link to complete...
              </div>
            )}

            <Button
              onClick={() => {
                setSubStep("install");
                setHasCode(false);
              }}
              variant="ghost"
              fullWidth
            >
              Back
            </Button>
          </>
        )}
      </div>
    );
  }

  return <InstalledView detail={view.detail} onRefresh={retry} />;
};
